import { Link } from "react-router-dom";
import { masteryFromEvidence } from "../../lib/progression";
import { usePortal } from "../../state/PortalContext";
import { Icon } from "../../components/Icon";
import {
  Button,
  Card,
  Pill,
  ProgressBar,
  SectionHeading,
} from "../../components/ui";

const masteryStyle = {
  "Not Started": "bg-slate-800 text-slate-400",
  Practicing: "bg-amber-400/15 text-amber-300",
  Competent: "bg-cyan-400/15 text-cyan-300",
  Applied: "bg-indigo-400/15 text-indigo-300",
  Mastered: "bg-emerald-400/15 text-emerald-300",
};

export function DashboardPage() {
  const { curriculum, progress, user } = usePortal();
  if (!user) return null;

  const recommended =
    curriculum.quests.find(
      (quest) =>
        quest.templateId !== "coming-soon" &&
        !progress.completedQuestIds.includes(quest.id),
    ) ??
    curriculum.quests.find((quest) => quest.templateId !== "coming-soon");
  const activeRegion = recommended
    ? curriculum.regions.find((region) => region.id === recommended.regionId)
    : curriculum.regions[0];
  const averageCompletion = Math.round(
    Object.values(progress.regionCompletion).reduce(
      (sum, value) => sum + value,
      0,
    ) / curriculum.regions.length,
  );
  const mastered = curriculum.competencies.filter(
    (competency) =>
      masteryFromEvidence(progress.competencyEvidence[competency.id] ?? 0) ===
      "Mastered",
  ).length;

  return (
    <div className="space-y-7">
      <SectionHeading
        eyebrow="Adventure Hub"
        title={`Welcome back, ${user.fullName.split(" ")[0]}`}
        detail="A quick view of your current expedition, growing competencies, and the next useful challenge."
        action={
          <Link to="/app/map">
            <Button variant="secondary">
              <Icon name="Map" size={18} />
              World map
            </Button>
          </Link>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: "Course explored",
            value: `${averageCompletion}%`,
            icon: "Compass",
            color: "text-cyan-300",
          },
          {
            label: "Current level",
            value: user.level,
            icon: "Sparkles",
            color: "text-amber-300",
          },
          {
            label: "Skills mastered",
            value: mastered,
            icon: "Award",
            color: "text-emerald-300",
          },
          {
            label: "Earned badges",
            value: progress.badges.length,
            icon: "Trophy",
            color: "text-violet-300",
          },
        ].map((stat) => (
          <Card key={stat.label} className="p-5">
            <div className="flex items-center justify-between">
              <span className={`rounded-xl bg-slate-950 p-3 ${stat.color}`}>
                <Icon name={stat.icon} size={22} />
              </span>
              <span className="font-display text-3xl font-bold text-white">
                {stat.value}
              </span>
            </div>
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              {stat.label}
            </p>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_.95fr]">
        <Card className="overflow-hidden">
          <div
            className="relative min-h-80 p-7 sm:p-9"
            style={{
              background: `radial-gradient(circle at 85% 10%, ${activeRegion?.color}33, transparent 45%)`,
            }}
          >
            <Pill tone="amber">Recommended next quest</Pill>
            <div className="mt-8 max-w-xl">
              <p
                className="text-sm font-extrabold"
                style={{ color: activeRegion?.color }}
              >
                {activeRegion?.name}
              </p>
              <h2 className="mt-2 font-display text-4xl font-bold leading-tight text-white">
                {recommended?.title}
              </h2>
              <p className="mt-4 leading-7 text-slate-300">
                {recommended?.storyHook}
              </p>
            </div>
            {recommended && (
              <Link to={`/app/quest/${recommended.id}`}>
                <Button variant="gold" className="mt-7">
                  <Icon name="Play" size={18} />
                  Continue expedition
                </Button>
              </Link>
            )}
            <Icon
              name={activeRegion?.icon ?? "Compass"}
              size={150}
              className="pointer-events-none absolute -bottom-5 -right-3 opacity-[0.06]"
            />
          </div>
        </Card>

        <Card className="p-7">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-retro text-[8px] tracking-widest text-indigo-300">
                CHARACTER PROGRESSION
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-white">
                Level {user.level}
              </h2>
            </div>
            <span className="rounded-2xl bg-indigo-500/15 p-3 text-indigo-300">
              <Icon name="Sparkles" size={24} />
            </span>
          </div>
          <div className="mt-8">
            <ProgressBar
              value={(user.xp / user.xpNext) * 100}
              label={`${user.xp} / ${user.xpNext} XP`}
            />
          </div>
          <div className="mt-7 grid grid-cols-2 gap-3">
            {progress.badges.slice(0, 4).map((badge) => (
              <div
                key={badge.id}
                className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-center"
              >
                <Icon
                  name={badge.icon}
                  size={24}
                  className="mx-auto text-amber-300"
                />
                <p className="mt-2 text-xs font-bold text-slate-200">
                  {badge.name}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-retro text-[8px] tracking-widest text-emerald-300">
              MASTERY HEATMAP
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-white">
              Your game-math skill codex
            </h2>
          </div>
          <p className="text-xs text-slate-500">
            Evidence replaces intimidating letter grades.
          </p>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {curriculum.competencies.map((competency) => {
            const evidence = progress.competencyEvidence[competency.id] ?? 0;
            const mastery = masteryFromEvidence(evidence);
            return (
              <div
                key={competency.id}
                className="rounded-2xl border border-slate-800 bg-slate-950/45 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-200">
                      {competency.name}
                    </p>
                    <p className="mt-1 line-clamp-1 text-xs text-slate-500">
                      {competency.description}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-lg px-2 py-1 text-[9px] font-extrabold ${masteryStyle[mastery]}`}
                  >
                    {mastery}
                  </span>
                </div>
                <div className="mt-3">
                  <ProgressBar
                    value={evidence}
                    color={
                      mastery === "Mastered"
                        ? "bg-emerald-400"
                        : mastery === "Applied"
                          ? "bg-indigo-400"
                          : mastery === "Competent"
                            ? "bg-cyan-400"
                            : "bg-amber-400"
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
